import React, { useState, useEffect } from 'react';
import { authService } from '../../../services/api';

const UserSecurity = ({ onNavigate }) => {
  const [securityData, setSecurityData] = useState({
    two_factor_enabled: false,
    is_account_locked: false,
    last_login_ip: '',
    failed_login_attempts: 0,
    has_password: false,
    has_google_account: false,
    is_google_oauth_only: false,
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    new_password_confirm: '',
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadSecurityData();
    loadUserSessions();
  }, []);

  const loadSecurityData = async () => {
    setLoading(true);
    try {
      const security = await authService.getUserSecurity();
      setSecurityData(security);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUserSessions = async () => {
    try {
      const sessionsData = await authService.getUserSessions();
      // Ensure sessions is always an array
      setSessions(Array.isArray(sessionsData) ? sessionsData : []);
    } catch (error) {
      console.error('Error loading sessions:', error);
      setSessions([]); // Set empty array on error
    }
  };

  const handleTwoFactorToggle = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    
    try {
      await authService.updateUserSecurity({
        two_factor_enabled: !securityData.two_factor_enabled
      });
      setSecurityData(prev => ({
        ...prev,
        two_factor_enabled: !prev.two_factor_enabled
      }));
      setMessage(
        securityData.two_factor_enabled 
          ? 'Two-factor authentication disabled' 
          : 'Two-factor authentication enabled'
      );
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTerminateSession = async (sessionId) => {
    try {
      await authService.terminateSession(sessionId);
      setSessions(prev => prev.filter(session => session.id !== sessionId));
      setMessage('Session terminated successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setError(error.message);
    }
  };

  const handlePasswordFieldChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
    if (passwordErrors[name]) {
      setPasswordErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setPasswordSaving(true);
    setPasswordErrors({});
    setError('');
    setMessage('');

    if (passwordForm.new_password.length < 8) {
      setPasswordErrors({ new_password: 'Password must be at least 8 characters.' });
      setPasswordSaving(false);
      return;
    }
    if (passwordForm.new_password !== passwordForm.new_password_confirm) {
      setPasswordErrors({ new_password_confirm: 'Passwords do not match.' });
      setPasswordSaving(false);
      return;
    }

    try {
      const result = await authService.setAccountPassword({
        currentPassword: passwordForm.current_password || undefined,
        newPassword: passwordForm.new_password,
        newPasswordConfirm: passwordForm.new_password_confirm,
      });
      setSecurityData((prev) => ({
        ...prev,
        has_password: result?.has_password ?? true,
        is_google_oauth_only: result?.is_google_oauth_only ?? false,
      }));
      setPasswordForm({ current_password: '', new_password: '', new_password_confirm: '' });
      setMessage(
        securityData.has_password
          ? 'Password changed successfully'
          : 'Password set successfully. You can now sign in with email and password.'
      );
      setTimeout(() => setMessage(''), 4000);
    } catch (err) {
      const fieldErrors = err.errors || {};
      if (typeof fieldErrors === 'object' && !fieldErrors.code) {
        const mapped = {};
        Object.keys(fieldErrors).forEach((key) => {
          const val = fieldErrors[key];
          mapped[key] = Array.isArray(val) ? val[0] : val;
        });
        setPasswordErrors(mapped);
      }
      setError(err.message || 'Failed to update password.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleTerminateAllSessions = async () => {
    if (window.confirm('Are you sure you want to terminate all other sessions? This will log you out of all other devices.')) {
      try {
        await authService.terminateAllSessions();
        setSessions(prev => prev.filter(session => session.is_current));
        setMessage('All other sessions terminated successfully');
        setTimeout(() => setMessage(''), 3000);
      } catch (error) {
        setError(error.message);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getDeviceInfo = (userAgent) => {
    // Simple device detection
    if (userAgent.includes('Mobile')) return 'Mobile';
    if (userAgent.includes('Tablet')) return 'Tablet';
    return 'Desktop';
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading security information...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {message && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
          {message}
        </div>
      )}

      {/* Password */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Password</h2>
        {securityData.is_google_oauth_only && (
          <p className="text-sm text-gray-600 mb-4">
            Your account uses Google sign-in. Set a password here to also sign in with your email.
          </p>
        )}
        <form onSubmit={handleSetPassword} className="space-y-4 max-w-md">
          {securityData.has_password && (
            <div>
              <label htmlFor="current_password" className="block text-sm font-medium text-gray-700 mb-1">
                Current password
              </label>
              <input
                id="current_password"
                name="current_password"
                type="password"
                autoComplete="current-password"
                value={passwordForm.current_password}
                onChange={handlePasswordFieldChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              {passwordErrors.current_password && (
                <p className="mt-1 text-sm text-red-600">{passwordErrors.current_password}</p>
              )}
            </div>
          )}
          <div>
            <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 mb-1">
              {securityData.has_password ? 'New password' : 'Password'}
            </label>
            <input
              id="new_password"
              name="new_password"
              type="password"
              autoComplete="new-password"
              value={passwordForm.new_password}
              onChange={handlePasswordFieldChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
              minLength={8}
            />
            {passwordErrors.new_password && (
              <p className="mt-1 text-sm text-red-600">{passwordErrors.new_password}</p>
            )}
          </div>
          <div>
            <label htmlFor="new_password_confirm" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm password
            </label>
            <input
              id="new_password_confirm"
              name="new_password_confirm"
              type="password"
              autoComplete="new-password"
              value={passwordForm.new_password_confirm}
              onChange={handlePasswordFieldChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
              minLength={8}
            />
            {passwordErrors.new_password_confirm && (
              <p className="mt-1 text-sm text-red-600">{passwordErrors.new_password_confirm}</p>
            )}
          </div>
          <button
            type="submit"
            disabled={passwordSaving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {passwordSaving
              ? 'Saving...'
              : securityData.has_password
                ? 'Change password'
                : 'Set password'}
          </button>
        </form>
      </div>

      {/* Security Status */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Security Status</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="text-sm font-medium text-gray-900">Two-Factor Authentication</h3>
                <p className="text-sm text-gray-500">
                  {securityData.two_factor_enabled ? 'Enabled' : 'Disabled'}
                </p>
              </div>
              <button
                onClick={handleTwoFactorToggle}
                disabled={saving}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  securityData.two_factor_enabled
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                {securityData.two_factor_enabled ? 'Disable' : 'Enable'}
              </button>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-900">Account Status</h3>
              <p className="text-sm text-gray-500">
                {securityData.is_account_locked ? 'Account Locked' : 'Account Active'}
              </p>
              {securityData.failed_login_attempts > 0 && (
                <p className="text-sm text-orange-600 mt-1">
                  Failed login attempts: {securityData.failed_login_attempts}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-900">Last Login IP</h3>
              <p className="text-sm text-gray-500 font-mono">
                {securityData.last_login_ip || 'Not available'}
              </p>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Security Tips</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Enable two-factor authentication for better security</li>
                      <li>Use a strong, unique password</li>
                      <li>Log out from devices you don't recognize</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Active Sessions */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Active Sessions</h2>
          {sessions.length > 1 && (
            <button
              onClick={handleTerminateAllSessions}
              className="px-4 py-2 bg-red-100 text-red-700 rounded-md text-sm font-medium hover:bg-red-200 transition-colors"
            >
              Terminate All Others
            </button>
          )}
        </div>

        {!sessions || sessions.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No active sessions found.</p>
        ) : (
          <div className="space-y-4">
            {sessions.map((session) => (
              <div key={session.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm8 0a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1V8zm0 4a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1v-2z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {getDeviceInfo(session.user_agent)}
                      </p>
                      <p className="text-sm text-gray-500">
                        IP: {session.ip_address} • Last activity: {formatDate(session.last_activity)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {session.is_current && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                      Current
                    </span>
                  )}
                  {!session.is_current && (
                    <button
                      onClick={() => handleTerminateSession(session.id)}
                      className="px-3 py-1 bg-red-100 text-red-700 rounded-md text-sm font-medium hover:bg-red-200 transition-colors"
                    >
                      Terminate
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSecurity;
